import {Component, OnInit} from '@angular/core';
import {SocialLoginNotice} from '../../enums/social-login-notice';

/**
 * Renders the informational notice an external sign-in can carry (`?notice=<code>`, see `SigningInComponent`).
 *
 * It lives on the LANDING, not on the loader that exchanged the handoff. The sign-in succeeded, so nothing here
 * may stand between the person and the app: this appears beside the page they were going to anyway, is
 * dismissible, and blocks nothing. That is the one thing the notice it replaces got wrong — it held the loader
 * until somebody pressed "Weiter".
 *
 * The parameter is stripped once read, exactly as `MigrationNoticeHostComponent` does with `?claimed=success`,
 * so a reload or a shared link does not re-announce something that was already said.
 */
@Component({
  selector: 'auth-sign-in-notice',
  standalone: true,
  template: `
    @if (message) {
      <div class="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 sm:p-4" role="status" aria-live="polite">
        <div class="w-full max-w-lg rounded-2xl border-[1px] border-theme-primary-100 bg-white p-4 shadow-xl">
          <div class="flex items-start gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-gray-900">{{ heading }}</p>
              <p class="mt-1 text-sm text-gray-600">{{ message }}</p>
            </div>

            <button type="button" (click)="dismiss()" aria-label="Hinweis schließen"
                    class="-m-1 shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600">
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    }
  `
})
export class SignInNoticeComponent implements OnInit {
  protected heading = '';
  protected message = '';

  ngOnInit(): void {
    const code = new URLSearchParams(location.search).get('notice');
    if (!code) return;

    const notice = SignInNoticeComponent.textFor(code);
    // An unknown code renders nothing rather than an empty card: a newer backend may send a notice this build
    // has no wording for, and a blank box says less than nothing.
    if (notice) {
      this.heading = notice.heading;
      this.message = notice.message;
    }

    // Stripped whether or not it was understood — an unrenderable code left in the URL would survive every
    // in-app navigation and reappear on the next reload.
    SignInNoticeComponent.stripFromUrl();
  }

  protected dismiss(): void {
    this.heading = '';
    this.message = '';
  }

  private static textFor(code: string): { heading: string; message: string } | null {
    switch (code) {
      case SocialLoginNotice.InvitationAlreadyHeld:
        // The second sentence used to promise the opposite — "nicht verbraucht und kann weiterhin eingelöst
        // werden". An invitation is now spent by any redemption that answers it, including one that added
        // nothing, so that reassurance became a false one. The first sentence stays: the point of this notice is
        // that a "nothing happened" outcome still ends in a working session. "eingelöst" is deliberate — it is
        // the word the admin sees against the invitation on the Zugänge screen.
        return {
          heading: 'Sie sind angemeldet',
          message: 'Die Einladung hat Ihrem Konto nichts hinzugefügt — Sie hatten diese Berechtigungen bereits. ' +
            'Sie gilt damit als eingelöst und kann nicht erneut verwendet werden.'
        };
      default:
        return null;
    }
  }

  private static stripFromUrl(): void {
    const url = new URL(location.href);
    url.searchParams.delete('notice');
    history.replaceState({}, '', url.toString());
  }
}
